// A small "which series does this chart plot" popover.
//
// It is the Meta counterpart of the control Google's multi-metric trend already
// carries in its panel toolbar — same placement, same shape of choice — but it
// takes a plain `{key,label}` list rather than being typed to a catalogue, so it
// is not a fork of Google's MetricPicker (which also carries that dashboard's
// attribute-column group and is typed to GoogleAdsMetricKey).
//
// Two rules it enforces, both about not leaving a chart in a nonsense state:
//   · never remove the LAST series — an empty titled chart reads as broken
//     rather than as a choice, and hiding the chart is what the chart's own tick
//     is for;
//   · never exceed `max` — past a handful of lines on one shared axis the
//     metrics stop being comparable.

import { useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useClickOutside } from '../../lib/useClickOutside';
import { cn } from '../../lib/utils';

export interface SeriesOption {
  key: string;
  label: string;
}

export function SeriesPicker({
  label,
  options,
  selected,
  onChange,
  max,
}: {
  label: string;
  options: readonly SeriesOption[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  max: number;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const set = new Set(selected);
  const atCap = set.size >= max;

  const toggle = (key: string): void => {
    if (set.has(key)) {
      if (set.size === 1) return; // keep at least one line on the chart
      onChange(options.filter((o) => o.key !== key && set.has(o.key)).map((o) => o.key));
      return;
    }
    if (atCap) return;
    // Preserve catalogue order, so the legend reads the same way every time.
    onChange(options.filter((o) => set.has(o.key) || o.key === key).map((o) => o.key));
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        {label} ({set.size}) <ChevronDown className="ml-1 h-3.5 w-3.5" />
      </Button>
      {open && (
        <div
          role="menu"
          className="scroll-subtle absolute right-0 z-30 mt-1 max-h-96 w-60 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
        >
          {options.map((o) => {
            const checked = set.has(o.key);
            const disabled = (!checked && atCap) || (checked && set.size === 1);
            return (
              <button
                key={o.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => toggle(o.key)}
                title={
                  !checked && atCap
                    ? `Up to ${max} at a time`
                    : checked && set.size === 1
                      ? 'At least one series stays on the chart'
                      : undefined
                }
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
                  disabled && 'cursor-not-allowed opacity-40',
                )}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {checked && <Check className="h-3.5 w-3.5" />}
                </span>
                <span className="min-w-0 truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
